import React, { useEffect, useMemo, useReducer, useRef, useState, forwardRef, useImperativeHandle, Suspense } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import PointCloudRenderer, { OrbitControls } from './PointCloudRenderer';
import { contentBox, drapedBox } from '../lib/camera-framing';
import FenceTraces from './FenceTraces';
import useTerrainSettings from '../hooks/useTerrainSettings';
import { useTheme } from '../contexts/ThemeContext';
import useDoiSettings from '../hooks/useDoiSettings';
import { makeDoiMask } from '../lib/inversion-results';
import { useViewSync } from '../contexts/ViewSyncContext';
import ViewSync3D from './ViewSync3D';
import VolumeIsoControls from './VolumeIsoControls';
import { EnableClipping, IsoSurfaceMesh, CuttingPlaneGizmo } from './IsoSurface3D';
import useIsoSurface from '../hooks/useIsoSurface';
import { planeNormal, planeSlice } from '../lib/iso-scene';
import { meshToObj, meshToStl } from '../lib/marching-cubes';
import { contextReducer, initialContextState, watchContext } from '../lib/webgl-context';
import VoxelModel, { VoxelSelectionContext, useVoxelSelection } from './VoxelModel';
import VoxelVolumeControls from './VoxelVolumeControls';
import VolumeSlices, { SliceContext } from './VolumeSlices';
import VolumeSliceControls from './VolumeSliceControls';
import SliceImageModal from './SliceImageModal';
import { DEFAULT_SLICES, buildGrid, slicesFor, modelFrame, columnSurface, slicePixels, toggleDynamic, formatProbe } from '../lib/volume-slices';
import VolumeColourControls from './VolumeColourControls';
import { FenceColourContext, useFenceScale } from './FenceColour';
import { fencePixels } from '../lib/fence-texture';
import { DEFAULT_COLOUR, MIN_LEVELS, MAX_LEVELS, combinedRange, hasLevelModels, makeColourScale, resizeOpacity } from '../lib/model-colour';
import { selectedLevelIndices } from '../lib/iso-levels';
import { selectVoxels, flatPositions, hasExtents } from '../lib/voxel-select';
import { volumeStats, cellsToXyzCsv } from '../lib/volume-stats';
import { downloadBlob } from '../lib/export';
import { localToLonLat, projectOnLine, xFromFrac, modelsExtentLonLat } from '../lib/view-sync';

const COLOR_MODE_LABELS = {
  viridis: 'Viridis (Depth-based)',
  height: 'Height Ramp',
  rgb: 'RGB',
  intensity: 'Intensity',
  classification: 'Classification',
};

/**
 * Fence diagram plane — one inversion model rendered as a vertical colored
 * plane at its geographic position (EarthImager 3D-style). Local coordinates
 * share the origin with the focused line's point cloud.
 */
function FencePlane({ entry, origin, opacity = 0.95, onHover, onHoverEnd, clippingPlanes }) {
  const doi = useDoiSettings(); // global DOI mask (Inversion panel): cells below the DOI are left transparent
  const scale = useFenceScale(entry); // the volume colour table (levels, table, banding, per-level opacity)
  const { texture, length, maxDepth, center, angle } = useMemo(() => {
    const cells = entry.cells || [];
    const model = entry.model;
    const [start, end] = entry.line;
    const mLat = 110540;
    const mLon = 111320 * Math.cos((origin.lat0 * Math.PI) / 180);
    const toLocal = ([lon, lat]) => [(lon - origin.lon0) * mLon, (lat - origin.lat0) * mLat];

    const s = toLocal(start), e = toLocal(end);
    const dxL = e[0] - s[0], dzL = e[1] - s[1];
    const length = Math.sqrt(dxL * dxL + dzL * dzL) || 1;
    const angle = Math.atan2(dzL, dxL);
    const center = [(s[0] + e[0]) / 2, (s[1] + e[1]) / 2];
    const maxDepth = model.depthRange[1] || 1;

    // Colour texture (stations x depth levels) from the shared model colour table; DOI-masked cells stay transparent
    const belowDoi = makeDoiMask(cells, { mask: doi.mask, threshold: doi.threshold, fallbackDoi: model.doi });
    const { w, h, data } = fencePixels(cells, scale, belowDoi);

    const texture = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
    texture.needsUpdate = true;
    texture.magFilter = THREE.NearestFilter;

    return { texture, length, maxDepth, center, angle };
  }, [entry, origin, doi.mask, doi.threshold, scale]);

  return (
    <mesh position={[center[0], -maxDepth / 2, center[1]]} rotation={[0, -angle, 0]}
      onPointerMove={onHover ? (e) => { e.stopPropagation(); onHover(entry, e.point); } : undefined}
      onPointerOut={onHoverEnd}>
      <planeGeometry args={[length, maxDepth]} />
      <meshBasicMaterial map={texture} side={THREE.DoubleSide} transparent opacity={opacity} clippingPlanes={clippingPlanes} />
    </mesh>
  );
}

/**
 * 3D inversion model (EarthImager 3D .out / InvRes; geometryKind grid|mesh3d) as blocks or a coloured
 * voxel point cloud (components/VoxelModel.js). Cells carry lon/lat from the model's GeoRef, so the
 * grid sits at its real position and is rotated to its real azimuth; same local frame as FencePlane
 * (x = east, y = -depth, z = north). The cells drawn come from the selection provided by the viewer.
 */
function VoxelCloud({ entry, opacity = 0.95, onHover, onHoverEnd, clippingPlanes }) {
  const { sel, outsideOpacity } = useVoxelSelection(entry.id);
  const positions = useMemo(() => (sel ? flatPositions(sel) : null), [sel]);
  if (!sel) return null;
  return <VoxelModel entry={entry} sel={sel} positions={positions} opacity={opacity} outsideOpacity={outsideOpacity}
    clippingPlanes={clippingPlanes} onHover={onHover} onHoverEnd={onHoverEnd} />;
}

/**
 * The cloud the viewer draws: the focused survey line's real geometry, else (demo mode only)
 * the sample cloud, else nothing. Dataset list rows carry no point data, so no points are invented.
 */
export function buildPointCloud(focusLine, demoMode = false) {
  if (focusLine?.coordinates?.length >= 2) {
    const cloud = buildLinePointCloud(focusLine);
    if (cloud) return cloud;
  }
  return demoMode ? buildDemoCloud() : emptyCloud();
}

/** No points at all (the real-data case with nothing loaded). */
export function emptyCloud() {
  return {
    positions: new Float32Array(0), colors: new Float32Array(0), intensity: new Float32Array(0),
    classification: new Uint8Array(0), pointCount: 0, bboxMin: [-50, 0, -50], bboxMax: [50, 40, 50],
  };
}

/** Demo-mode sample cloud: 1000 random points on or above the reference grid (y >= 0). */
export function buildDemoCloud(count = 1000, rand = Math.random) {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const intensity = new Float32Array(count);
  const classification = new Uint8Array(count);
  for (let i = 0; i < count; i++) {
    positions[i * 3] = (rand() - 0.5) * 100;
    positions[i * 3 + 1] = rand() * 40;
    positions[i * 3 + 2] = (rand() - 0.5) * 100;
    const t = positions[i * 3 + 1] / 40;
    colors[i * 3] = t;
    colors[i * 3 + 1] = 0.5;
    colors[i * 3 + 2] = 1 - t;
    intensity[i] = rand();
    classification[i] = Math.floor(rand() * 5);
  }
  return { positions, colors, intensity, classification, pointCount: count, bboxMin: [-50, 0, -50], bboxMax: [50, 40, 50] };
}


/**
 * Convert a survey line's lon/lat coordinates into a subsurface point cloud.
 * X/Z come from the ACTUAL line geometry (local meters around the line center);
 * Y is depth — stations along the line get pseudo-section depth levels
 * (depth of investigation ≈ line length / 5, standard for ERT).
 */
function buildLinePointCloud(focusLine) {
  const coords = focusLine.coordinates || [];
  if (coords.length < 2) return null;

  // Local tangent-plane projection around the line centroid
  const lat0 = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  const lon0 = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const mLat = 110540;
  const mLon = 111320 * Math.cos((lat0 * Math.PI) / 180);
  const toLocal = ([lon, lat]) => [(lon - lon0) * mLon, (lat - lat0) * mLat];

  const local = coords.map(toLocal);

  // Cumulative distance along the line
  const cum = [0];
  for (let i = 1; i < local.length; i++) {
    const dx = local[i][0] - local[i - 1][0];
    const dz = local[i][1] - local[i - 1][1];
    cum.push(cum[i - 1] + Math.sqrt(dx * dx + dz * dz));
  }
  const totalLen = cum[cum.length - 1] || 1;

  // Interpolate a position at distance d along the real path
  const positionAt = (d) => {
    let i = 1;
    while (i < cum.length && cum[i] < d) i++;
    if (i >= cum.length) return local[local.length - 1];
    const t = (d - cum[i - 1]) / ((cum[i] - cum[i - 1]) || 1);
    return [
      local[i - 1][0] + (local[i][0] - local[i - 1][0]) * t,
      local[i - 1][1] + (local[i][1] - local[i - 1][1]) * t,
    ];
  };

  const nStations = Math.min(96, Math.max(24, Math.round(totalLen / 25)));
  const nLevels = 14;
  const maxDepth = Math.max(10, totalLen / 5); // ERT depth of investigation
  const pointCount = nStations * nLevels;

  const positions = new Float32Array(pointCount * 3);
  const colors = new Float32Array(pointCount * 3);
  const intensity = new Float32Array(pointCount);
  const classification = new Uint8Array(pointCount);

  // Deterministic synthetic resistivity field along the section
  // (until measured inversion values are wired in — see TODO 3.1)
  const value = (s, dNorm) =>
    0.5 +
    0.35 * Math.sin((s / totalLen) * Math.PI * 3 + 0.7) * Math.cos(dNorm * Math.PI * 1.5) +
    0.15 * Math.sin((s / totalLen) * Math.PI * 8) * (1 - dNorm);

  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;

  for (let si = 0; si < nStations; si++) {
    const d = (si / (nStations - 1)) * totalLen;
    const [x, z] = positionAt(d);
    minX = Math.min(minX, x); maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z);
    for (let li = 0; li < nLevels; li++) {
      const idx = si * nLevels + li;
      const dNorm = li / (nLevels - 1);
      // Pseudo-section: shallower coverage near line ends
      const endTaper = Math.min(d, totalLen - d) / (totalLen / 2);
      const depth = 1.5 + dNorm * maxDepth * Math.max(0.15, Math.min(1, endTaper * 1.6));

      positions[idx * 3] = x;
      positions[idx * 3 + 1] = -depth;
      positions[idx * 3 + 2] = z;

      const v = Math.max(0, Math.min(1, value(d, dNorm)));
      // Cool→warm colormap by value
      colors[idx * 3] = v;
      colors[idx * 3 + 1] = 0.35 + 0.3 * (1 - Math.abs(v - 0.5) * 2);
      colors[idx * 3 + 2] = 1 - v;

      intensity[idx] = v;
      classification[idx] = Math.floor(dNorm * 4);
    }
  }

  const clickLocal = focusLine.clickPoint ? toLocal(focusLine.clickPoint) : [0, 0];

  return {
    positions, colors, intensity, classification, pointCount,
    bboxMin: [minX, -maxDepth, minZ],
    bboxMax: [maxX, 0, maxZ],
    lineLength: totalLen,
    maxDepth,
    lookAt: [clickLocal[0], -maxDepth / 3, clickLocal[1]],
  };
}

/**
 * Camera fit for a set of models in the shared local frame: the centre of their extent and a
 * distance that frames it (fence line ends or voxel cell lon/lat; depth from model.depthRange).
 * Null when none of them has a position.
 */
export function fitModelsView(models, origin) {
  if (!origin || !models?.length) return null;
  const mLat = 110540;
  const mLon = 111320 * Math.cos((origin.lat0 * Math.PI) / 180);
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, depth = 0;
  const add = ([lon, lat]) => {
    const x = (lon - origin.lon0) * mLon, z = (lat - origin.lat0) * mLat;
    x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
  };
  models.forEach((m) => {
    if (m.line?.length === 2) m.line.forEach(add);
    else if (m.cells?.[0]?.lon !== undefined) m.cells.forEach(c => add([c.lon, c.lat]));
    else return;
    depth = Math.max(depth, m.model?.depthRange?.[1] || 0);
  });
  if (!Number.isFinite(x0)) return null;
  const size = Math.max(x1 - x0, z1 - z0, depth);
  return { target: [(x0 + x1) / 2, -depth / 3, (z0 + z1) / 2], distance: Math.max(80, size * 1.2) };
}

/** Positions the camera relative to the focused line and looks at the 2D click point. */
function CameraRig({ target, distance, seq = 0 }) {
  const { camera } = useThree();
  // Primitive deps: a recomputed (equal) target must not snap the camera back while orbiting
  const has = !!target;
  const [tx, ty, tz] = target || [];
  useEffect(() => {
    if (!has) return;
    camera.position.set(tx + distance * 0.6, distance * 0.5, tz + distance);
    camera.lookAt(tx, ty, tz);
    camera.updateProjectionMatrix();
  }, [camera, has, tx, ty, tz, distance, seq]);
  return null;
}

/** Grabs the renderer's canvas element so the page can request a PNG snapshot. */
function CanvasCapture({ canvasElRef }) {
  const { gl } = useThree();
  useEffect(() => { canvasElRef.current = gl.domElement; }, [gl, canvasElRef]);
  return null;
}

// ─── Compact, collapsible docked panel for volume-exploration controls ───
// A normal side panel (not an absolute-positioned overlay) — the canvas is
// never obscured by it.
function VolumePanel({
  collapsed, onToggleCollapsed,
  threshold, setThreshold,
  sliceAxis, setSliceAxis,
  slicePos, setSlicePos,
  sliceThickness, setSliceThickness,
  cropX, setCropX,
  cropZ, setCropZ,
  showFences, setShowFences,
  fenceOpacity, setFenceOpacity,
  usableFences,
  filteredCount, totalCount,
  onResetFilters,
  extra,
}) {
  const { C } = useTheme();
  if (collapsed) {
    return (
      <div className="w-10 flex flex-col items-center pt-2 flex-shrink-0" style={{ background: C.bg2, borderLeft: `1px solid ${C.border}` }}>
        <button onClick={onToggleCollapsed} title="Expand Volume Tools"
          className="w-7 h-7 rounded-md bg-accent-soft border border-accent-soft text-accent cursor-pointer flex items-center justify-center text-sm">
          🔪
        </button>
        <span className="text-[8px] mt-1" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', color: C.text3 }}>Volume</span>
      </div>
    );
  }

  return (
    <div className="w-[240px] flex flex-col flex-shrink-0 overflow-y-auto" style={{ background: C.bg2, borderLeft: `1px solid ${C.border}` }}>
      <div className="px-3 py-2.5 text-[12px] font-semibold flex items-center justify-between" style={{ borderBottom: `1px solid ${C.border}`, color: C.text0 }}>
        Volume Tools
        <button onClick={onToggleCollapsed} className="hover:text-slate-300 text-sm px-1" style={{ color: C.text3 }} title="Collapse">›</button>
      </div>

      <div className="p-3 space-y-4 text-[11px]">
        {/* Threshold */}
        <div>
          <div className="text-[10px] mb-1" style={{ color: C.text2 }}>
            Visible range: {(threshold[0] * 100).toFixed(0)}–{(threshold[1] * 100).toFixed(0)}%
            {filteredCount !== totalCount &&
              <span className="text-amber-400"> · {filteredCount.toLocaleString()} shown</span>}
          </div>
          <input type="range" min={0} max={1} step={0.02} value={threshold[0]}
            onChange={e => setThreshold(t => [Math.min(parseFloat(e.target.value), t[1]), t[1]])}
            className="w-full accent-cyan-400" />
          <input type="range" min={0} max={1} step={0.02} value={threshold[1]}
            onChange={e => setThreshold(t => [t[0], Math.max(parseFloat(e.target.value), t[0])])}
            className="w-full accent-cyan-400" />
        </div>

        {/* Slice plane */}
        <div>
          <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Slice plane</div>
          <div className="flex gap-1 mb-1">
            {[['none', 'Off'], ['depth', 'Horiz'], ['x', 'Vert X'], ['z', 'Vert Z']].map(([id, label]) => (
              <button key={id} onClick={() => setSliceAxis(id)}
                className={`flex-1 px-1 py-1 rounded text-[10px] cursor-pointer ${sliceAxis === id ? 'bg-accent-soft text-accent border border-accent-soft' : 'border border-transparent'}`}
                style={sliceAxis === id ? undefined : { background: C.bg3, color: C.text2 }}>
                {label}
              </button>
            ))}
          </div>
          {sliceAxis !== 'none' && (
            <>
              <div className="text-[10px]" style={{ color: C.text2 }}>Position (step through)</div>
              <input type="range" min={0} max={1} step={0.02} value={slicePos}
                onChange={e => setSlicePos(parseFloat(e.target.value))}
                className="w-full accent-amber-400" />
              <div className="text-[10px]" style={{ color: C.text2 }}>Thickness: {(sliceThickness * 100).toFixed(0)}%</div>
              <input type="range" min={0.04} max={0.5} step={0.02} value={sliceThickness}
                onChange={e => setSliceThickness(parseFloat(e.target.value))}
                className="w-full accent-amber-400" />
            </>
          )}
        </div>

        {/* Sub-volume crop */}
        <div>
          <div className="text-[10px] mb-1" style={{ color: C.text2 }}>Crop X: {(cropX[0] * 100).toFixed(0)}–{(cropX[1] * 100).toFixed(0)}%</div>
          <input type="range" min={0} max={1} step={0.02} value={cropX[0]}
            onChange={e => setCropX(c => [Math.min(parseFloat(e.target.value), c[1]), c[1]])}
            className="w-full accent-emerald-400" />
          <input type="range" min={0} max={1} step={0.02} value={cropX[1]}
            onChange={e => setCropX(c => [c[0], Math.max(parseFloat(e.target.value), c[0])])}
            className="w-full accent-emerald-400" />
          <div className="text-[10px] mb-1 mt-1" style={{ color: C.text2 }}>Crop Z: {(cropZ[0] * 100).toFixed(0)}–{(cropZ[1] * 100).toFixed(0)}%</div>
          <input type="range" min={0} max={1} step={0.02} value={cropZ[0]}
            onChange={e => setCropZ(c => [Math.min(parseFloat(e.target.value), c[1]), c[1]])}
            className="w-full accent-emerald-400" />
          <input type="range" min={0} max={1} step={0.02} value={cropZ[1]}
            onChange={e => setCropZ(c => [c[0], Math.max(parseFloat(e.target.value), c[0])])}
            className="w-full accent-emerald-400" />
        </div>

        {/* Fence diagrams */}
        {usableFences.length > 0 && (
          <div>
            <label className="flex items-center gap-2 text-[10px] mb-1 cursor-pointer" style={{ color: C.text1 }}>
              <input type="checkbox" checked={showFences} onChange={e => setShowFences(e.target.checked)}
                className="accent-purple-400" />
              Fence diagrams ({usableFences.length} section{usableFences.length > 1 ? 's' : ''})
            </label>
            {showFences && (
              <>
                <div className="text-[10px]" style={{ color: C.text2 }}>Fence opacity: {(fenceOpacity * 100).toFixed(0)}%</div>
                <input type="range" min={0.2} max={1} step={0.05} value={fenceOpacity}
                  onChange={e => setFenceOpacity(parseFloat(e.target.value))}
                  className="w-full accent-purple-400" />
              </>
            )}
          </div>
        )}

        {extra}

        <button onClick={onResetFilters}
          className="w-full py-1.5 rounded hover:bg-white/10 text-[10px] cursor-pointer border"
          style={{ background: C.bg3, color: C.text1, borderColor: C.border }}>
          Reset all
        </button>
      </div>
    </div>
  );
}

/**
 * 3D Subsurface Viewer Component
 * Integrated with AGI's Point Cloud Visualization
 * Source: github.com/Advanced-Geosciences-Inc/ultimate-point-cloud
 *
 * Fully controlled by the parent workbench (frontend/src/pages/Viewer3D.js):
 * every volume-exploration value is passed down as a prop,
 * and the ribbon's Volume tab plus the docked Volume panel below
 * are the only places those controls live — nothing floats over the canvas.
 */
const NO_MODELS = []; // stable defaults: a new [] per render would defeat the memos keyed on them
const NO_DATASETS = [];
const NO_SEL = {};
const noop = () => {};
const GRID_ARGS = [200, 20, '#3A7A7C', '#666'];
const AXES_ARGS = [50];

const SubsurfaceViewer3D = forwardRef(function SubsurfaceViewer3D({
  datasets = NO_DATASETS,
  focusLine = null, // { name, method, coordinates: [[lon,lat],...], clickPoint: [lon,lat] } from 2D map
  fenceModels = NO_MODELS, // inversion models [{ id, name, model, cells, line }] → fence diagram planes
  sceneExtras = null, // ({ origin }) => extra three.js nodes in the shared local frame (boreholes)
  textureAttribution = null, // source credit for a basemap texture draped on the terrain
  terrainDraped = false, // terrain/TerrainMesh draws the fences hung from the DEM: hide the flat ones
  terrainInfo = null, // { refElev, minElev, maxElev, plan } of the draped terrain (TerrainMesh onInfo): the presets frame the draped box; keep stable
  center = { lat: 30.4515, lng: -97.7989 }, // AGI HQ Austin
  style = { width: '100%', height: '100%' },
  showPointCloud = true,
  demoMode = false, // sample point cloud when there is no data; off by default (V.29)
  colorMode = 'viridis',
  cameraPresetRequest = null, // { id: 'top'|'side'|'perspective'|'reset', seq }
  contentExtents = null, // lon/lat extents [{ minLon, maxLon, minLat, maxLat }] of the extra scene content (dataset clouds, scatter): camera presets frame them too; keep the array stable
  fitModelIds = null, // stored-model ids (entry ids) the opening camera fits to (/3d?dataset= / ?model=)
  // Volume exploration — fully controlled by the parent page
  threshold, setThreshold,
  sliceAxis, setSliceAxis,
  slicePos, setSlicePos,
  sliceThickness, setSliceThickness,
  cropX, setCropX,
  cropZ, setCropZ,
  showVolumePanel, setShowVolumePanel,
  showFences, setShowFences,
  fenceOpacity, setFenceOpacity,
  // scene elements (hooks/useViewer3dView): reference grid off by default
  showGrid = false, showAxes = true, showTraces = true, showSections = true, showVolumes = true, showOverlay = true,
  renderMode = 'blocks', setRenderMode = noop, // 3D models: 'blocks' | 'points'
  outsideOpacity = 0, setOutsideOpacity = noop, // opacity of cells outside the visible range
  onResetVolumeFilters,
}, ref) {
  const canvasElRef = useRef(null);
  const orbitApiRef = useRef(null); // { getPose, setPose } of the orbit controls (saved view, turntable)
  const { C } = useTheme();
  // WebGL context loss: the canvas is re-mounted (key = epoch) on restore or click
  const [glState, glDispatch] = useReducer(contextReducer, initialContextState);
  const glCleanup = useRef(null);
  useEffect(() => () => { if (glCleanup.current) glCleanup.current(); }, []);
  const targetRef = useRef(new THREE.Vector3(0, 0, 0)); // orbit target, shared with the camera sync
  const { hovered, setHover, clearHover, sectionLine, syncCamera, camera: syncedCamera, publishCamera } = useViewSync();

  // Shared local origin: focused line centroid, else first fence line centroid.
  // Memoised on the models list: a fresh array per render would make a new origin per render,
  // and TerrainMesh (keyed on origin + extent) would rebuild its mesh on every re-render — in
  // the docked panel, which re-renders with the map, that was a build loop.
  const usableFences = React.useMemo(() => fenceModels.filter(f => f.line?.length === 2 && f.cells?.length), [fenceModels]);
  const voxelModels = React.useMemo(() => fenceModels.filter(f => ['grid', 'mesh3d'].includes(f.model?.geometryKind) && f.cells?.[0]?.lon !== undefined), [fenceModels]);
  const fenceOrigin = React.useMemo(() => {
    const coords = focusLine?.coordinates?.length >= 2
      ? focusLine.coordinates
      : usableFences[0]?.line
        || (voxelModels[0] ? voxelModels[0].cells.slice(0, 5000).map(c => [c.lon, c.lat]) : null);
    if (!coords) return null;
    return {
      lat0: coords.reduce((s, c) => s + c[1], 0) / coords.length,
      lon0: coords.reduce((s, c) => s + c[0], 0) / coords.length,
    };
  }, [focusLine, usableFences, voxelModels]);

  // Cells drawn per 3D model (DOI, visible range, slice, crop); the models, the volume readout and the XYZ export share it
  const doi = useDoiSettings();
  const [volModelId, setVolModelId] = useState(null);
  // Contour levels + colour table shared by blocks, points and iso-surfaces (min/max null = each model's data range)
  const [colour, setColourState] = useState(DEFAULT_COLOUR);
  const [iso, setIso] = useState({ enabled: false, modelId: null, every: 3, picked: null, colorMode: 'level', opacity: 0.85 });
  const setColour = React.useCallback((p) => {
    setColourState((s) => {
      const next = { ...s, ...p };
      if (p.count !== undefined && next.opacity) next.opacity = resizeOpacity(next.opacity, Math.max(MIN_LEVELS, Math.min(MAX_LEVELS, Math.round(next.count))));
      return next;
    });
    setIso((s) => (s.picked ? { ...s, picked: null } : s)); // picks index the old levels
  }, []);
  // Levels apply to all models: blocks, points, iso-surfaces, slices, fences and the cutting plane share one range (empty Min/Max = the combined data range)
  const allRange = React.useMemo(() => combinedRange(fenceModels), [fenceModels]);
  const allScale = React.useMemo(() => makeColourScale(colour, allRange), [colour, allRange]);
  const voxelSel = React.useMemo(() => {
    if (!fenceOrigin || !voxelModels.length) return NO_SEL;
    const out = {};
    voxelModels.forEach((m) => {
      out[m.id] = selectVoxels(m, fenceOrigin, {
        doi: { mask: doi.mask, threshold: doi.threshold }, mode: renderMode, colour: allScale, outsideOpacity,
        threshold, sliceAxis, slicePos, sliceThickness, cropX, cropZ,
      });
    });
    return out;
  }, [voxelModels, fenceOrigin, doi.mask, doi.threshold, renderMode, allScale, outsideOpacity, threshold, sliceAxis, slicePos, sliceThickness, cropX, cropZ]);
  const voxelCtx = React.useMemo(() => ({ models: voxelSel, outsideOpacity }), [voxelSel, outsideOpacity]);
  const volEntry = voxelModels.find((m) => m.id === volModelId) || voxelModels[0] || null;
  const volSel = volEntry ? voxelSel[volEntry.id] || null : null;
  const volStats = React.useMemo(() => (volSel ? volumeStats(volSel.popCells, { inRange: (c, i) => volSel.inMask[i] === 1, alpha: (c, i) => volSel.popAlpha[i] }) : null), [volSel]);
  // Slices through the chosen model (static / dynamic, lib/volume-slices.js); drawn flat here, or inside the terrain group when draped (SliceContext)
  const [slices, setSlices] = useState(DEFAULT_SLICES);
  const [probe, setProbe] = useState(null); // { text, point } of the block clicked on a slice
  const [show2d, setShow2d] = useState(false);
  const sliceGrid = React.useMemo(() => (volSel && slices.mode !== 'off' ? buildGrid(volSel.cells) : null), [volSel, slices.mode]);
  const sliceItems = React.useMemo(() => (sliceGrid ? slicesFor(volSel.cells, slices, sliceGrid) : []), [sliceGrid, volSel, slices]);
  const sliceFrame = React.useMemo(() => (sliceGrid && volSel.cells.length ? modelFrame(volSel.cells, flatPositions(volSel), columnSurface(volSel.cells, sliceGrid)) : null), [sliceGrid, volSel]);
  const sliceExtent = React.useMemo(() => (sliceGrid ? {
    x: sliceGrid.bounds.x[1] - sliceGrid.bounds.x[0], y: sliceGrid.bounds.y[1] - sliceGrid.bounds.y[0], z: sliceGrid.bounds.d[1] - sliceGrid.bounds.d[0],
  } : null), [sliceGrid]);
  const onSlicePick = React.useCallback((cell, point) => setProbe({ text: formatProbe(cell), point }), []);
  const onSliceDrag = React.useCallback((axis, pos) => setSlices((st) => ({ ...st, dyn: { ...st.dyn, [axis]: { ...st.dyn[axis], pos } } })), []);
  const slicing = slices.mode !== 'off' && sliceItems.length > 0;
  const onCanvasKey = React.useCallback((e) => {
    if (e.ctrlKey || e.metaKey || e.altKey || !['x', 'y', 'z'].includes(e.key.toLowerCase())) return;
    if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'SELECT') return;
    setSlices((st) => toggleDynamic(st, e.key.toLowerCase()));
  }, []);
  const slice2d = show2d && slices.mode === 'static' && sliceItems.length === 1 ? sliceItems[0] : null;
  const slice2dPixels = React.useMemo(() => (slice2d ? slicePixels(slice2d.slice, volSel, outsideOpacity) : null), [slice2d, volSel, outsideOpacity]);
  const exportXyz = React.useCallback(() => {
    if (!volSel || !volEntry) return;
    const cells = volSel.popCells.filter((c, i) => outsideOpacity > 0 || volSel.inMask[i] === 1);
    const name = (volEntry.model?.name || 'model').replace(/[^\w.-]+/g, '_');
    downloadBlob(new Blob([cellsToXyzCsv(cells)], { type: 'text/csv' }), `${name}-xyz.csv`);
  }, [volSel, volEntry, outsideOpacity]);

  // Hover in the 3D scene -> shared hover (map marker; section owner reads it from context)
  const handleHover3D = React.useCallback((entry, point) => {
    if (!fenceOrigin) return;
    const lonLat = localToLonLat(point.x, point.z, fenceOrigin);
    let x = null;
    if (entry.line?.length === 2) {
      const proj = projectOnLine(entry.line, lonLat);
      if (proj) x = xFromFrac({ coordinates: entry.line, xRange: entry.model?.xRange }, proj.frac);
    }
    setHover({ source: '3d', modelId: entry.id, x, depth: -point.y, lonLat });
  }, [fenceOrigin, setHover]);
  const modelFit = React.useMemo(
    () => (fitModelIds?.length ? fitModelsView(fenceModels.filter(f => fitModelIds.includes(f.id)), fenceOrigin) : null),
    [fitModelIds, fenceModels, fenceOrigin]);
  // Sync camera (docked 3D panel): a map target far from every model fits all of them instead
  const modelExtent = React.useMemo(() => modelsExtentLonLat(fenceModels), [fenceModels]);
  const allFit = React.useMemo(() => fitModelsView(fenceModels, fenceOrigin), [fenceModels, fenceOrigin]);
  const [syncFit, setSyncFit] = useState(null);
  const onSyncFit = React.useCallback(() => {
    if (!allFit) return null;
    targetRef.current.set(allFit.target[0], allFit.target[1], allFit.target[2]);
    setSyncFit((p) => ({ ...allFit, seq: (p?.seq || 0) + 1 }));
    return allFit.target;
  }, [allFit]);
  const sectionDepth = Math.max(10, ...usableFences.map(f => f.model?.depthRange?.[1] || 0));

  // Convert datasets to point cloud data format
  const pointCloudData = React.useMemo(() => buildPointCloud(focusLine, demoMode), [focusLine, demoMode]);

  // Apply threshold / slice plane / crop filters to the point cloud
  const filteredCloud = React.useMemo(() => {
    const src = pointCloudData;
    const noFilter = threshold[0] <= 0 && threshold[1] >= 1 && sliceAxis === 'none'
      && cropX[0] <= 0 && cropX[1] >= 1 && cropZ[0] <= 0 && cropZ[1] >= 1;
    if (noFilter) return src;

    const [bx0, by0, bz0] = src.bboxMin;
    const [bx1, by1, bz1] = src.bboxMax;
    const spanX = bx1 - bx0 || 1, spanY = by1 - by0 || 1, spanZ = bz1 - bz0 || 1;

    const keep = [];
    for (let i = 0; i < src.pointCount; i++) {
      const inten = src.intensity[i];
      if (inten < threshold[0] || inten > threshold[1]) continue;

      const x = src.positions[i * 3], y = src.positions[i * 3 + 1], z = src.positions[i * 3 + 2];
      const fx = (x - bx0) / spanX, fy = (y - by0) / spanY, fz = (z - bz0) / spanZ;
      if (fx < cropX[0] || fx > cropX[1] || fz < cropZ[0] || fz > cropZ[1]) continue;

      if (sliceAxis !== 'none') {
        const f = sliceAxis === 'depth' ? fy : sliceAxis === 'x' ? fx : fz;
        if (Math.abs(f - slicePos) > sliceThickness / 2) continue;
      }
      keep.push(i);
    }

    const positions = new Float32Array(keep.length * 3);
    const colors = new Float32Array(keep.length * 3);
    const intensity = new Float32Array(keep.length);
    const classification = new Uint8Array(keep.length);
    keep.forEach((srcIdx, outIdx) => {
      for (let k = 0; k < 3; k++) {
        positions[outIdx * 3 + k] = src.positions[srcIdx * 3 + k];
        colors[outIdx * 3 + k] = src.colors[srcIdx * 3 + k];
      }
      intensity[outIdx] = src.intensity[srcIdx];
      classification[outIdx] = src.classification[srcIdx];
    });

    return { ...src, positions, colors, intensity, classification, pointCount: keep.length };
  }, [pointCloudData, threshold, sliceAxis, slicePos, sliceThickness, cropX, cropZ]);

  // ── Iso-surface and cutting plane (docs/TODO.md 3.8) ──
  const [cut, setCut] = useState({ enabled: false, azimuth: 90, dip: 90, offset: 0, flip: false, showSlice: true });
  const isoEntry = voxelModels.find(m => m.id === iso.modelId) || voxelModels[0] || null;
  const isoScale = useMemo(() => makeColourScale(colour, allRange), [colour, allRange]);
  const isoLevels = useMemo(() => (
    isoScale ? selectedLevelIndices(isoScale.levels.length, iso.every, iso.picked).map((index) => ({ index, value: isoScale.levels[index], rgb: isoScale.table[index] })) : []
  ), [isoScale, iso.every, iso.picked]);
  const fenceScale = allScale;
  const volRange = allRange;
  const volScale = allScale;
  const isoInfo = useIsoSurface({
    entry: isoEntry, origin: fenceOrigin, levels: isoLevels,
    enabled: iso.enabled || (cut.enabled && cut.showSlice), extract: iso.enabled,
  });
  const cutGeom = useMemo(() => {
    const b = isoInfo.bounds || { min: pointCloudData.bboxMin, max: pointCloudData.bboxMax };
    const mid = [0, 1, 2].map(k => (b.min[k] + b.max[k]) / 2);
    const span = Math.max(20, ...[0, 1, 2].map(k => b.max[k] - b.min[k]));
    const n0 = planeNormal(cut.azimuth, cut.dip);
    const centre = mid.map((m, k) => m + n0[k] * cut.offset);
    const nClip = (cut.flip ? n0.map(q => -q) : n0);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(new THREE.Vector3(...nClip), new THREE.Vector3(...centre));
    return { n0, centre, plane, size: span * 1.3, range: span * 0.75 };
  }, [isoInfo.bounds, pointCloudData, cut.azimuth, cut.dip, cut.offset, cut.flip]);
  const clippingPlanes = useMemo(() => (cut.enabled ? [cutGeom.plane] : []), [cut.enabled, cutGeom]);
  // Slice state for the draped terrain group (TerrainMesh renders the slices and hides the sliced model's blocks there)
  const sliceCtx = useMemo(() => (slicing && showFences && volEntry ? {
    items: sliceItems, sel: volSel, frame: sliceFrame, grid: sliceGrid, extent: sliceExtent, outsideOpacity, clippingPlanes, probe,
    onPick: onSlicePick, onDrag: onSliceDrag, hideModelId: slices.hideBlocks ? volEntry.id : null,
  } : null), [slicing, showFences, volEntry, sliceItems, volSel, sliceFrame, sliceGrid, sliceExtent, outsideOpacity, clippingPlanes, probe, onSlicePick, onSliceDrag, slices.hideBlocks]);
  const sliceTexture = useMemo(() => {
    if (!(cut.enabled && cut.showSlice && isoInfo.grid && isoInfo.aff)) return null;
    // cells below the depth of investigation stay transparent, as on blocks and fences
    const belowDoi = makeDoiMask(isoEntry?.cells || [], { mask: doi.mask, threshold: doi.threshold, fallbackDoi: isoEntry?.model?.doi });
    return planeSlice(isoInfo.grid, isoInfo.aff, { centre: cutGeom.centre, normal: cutGeom.n0 }, cutGeom.size, 96, undefined, isoScale, belowDoi);
  }, [cut.enabled, cut.showSlice, isoInfo.grid, isoInfo.aff, cutGeom, isoScale, isoEntry, doi.mask, doi.threshold]);
  const exportIso = React.useCallback((fmt) => {
    if (!isoInfo.mesh) return;
    const name = `isosurface-${(isoEntry?.model?.name || 'model').replace(/[^\w.-]+/g, '_')}`;
    const text = fmt === 'stl' ? meshToStl(isoInfo.mesh, name) : meshToObj(isoInfo.mesh, name);
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
  }, [isoInfo.mesh, isoEntry]);

  // Camera command enriched with the current scene's look-at target
  // Bounding box of what is drawn (models, extra content; the point cloud when nothing else): the presets frame it
  const ve3d = useTerrainSettings().ve3d;
  const flatBox = useMemo(
    () => contentBox(fenceModels, fenceOrigin ? { lon0: fenceOrigin.lon0, lat0: fenceOrigin.lat0 } : null, contentExtents,
      showPointCloud && pointCloudData.pointCount > 0 ? { min: pointCloudData.bboxMin, max: pointCloudData.bboxMax } : null),
    [fenceModels, fenceOrigin, contentExtents, showPointCloud, pointCloudData]);
  // with the DEM drape on, the content sits at terrain elevation (x vertical exaggeration) in TerrainMesh's frame
  const drawnBox = useMemo(
    () => (terrainDraped && terrainInfo && flatBox ? drapedBox(flatBox, { ...terrainInfo, ve: Number(ve3d) || 1 }) : flatBox),
    [flatBox, terrainDraped, terrainInfo, ve3d]);
  // read through a ref: a box that changes later (extra content loading) must not re-fire the camera command
  const drawnBoxRef = useRef(null);
  drawnBoxRef.current = drawnBox;
  const cameraCommand = useMemo(() => {
    if (!cameraPresetRequest) return null;
    return { ...cameraPresetRequest, box: drawnBoxRef.current, lookAt: pointCloudData.lookAt || modelFit?.target || null, lineLength: pointCloudData.lineLength || (modelFit ? modelFit.distance / 0.8 : undefined), topTilt: usableFences.length > 0 ? true : undefined };
  }, [cameraPresetRequest, pointCloudData, modelFit, usableFences]);

  const orbitBounds = useMemo(() => ({ min: pointCloudData.bboxMin, max: pointCloudData.bboxMax }), [pointCloudData]);

  // Export a PNG snapshot of the current canvas, or a JSON summary of the scene
  useImperativeHandle(ref, () => ({
    getCameraPose: () => orbitApiRef.current?.getPose() || null,
    setCameraPose: (pose) => { orbitApiRef.current?.setPose(pose); },
    // Geographic anchor of the scene's local frame (saved with the camera) and the box of the drawn content
    getSceneOrigin: () => (fenceOrigin ? { lon: fenceOrigin.lon0, lat: fenceOrigin.lat0 } : null),
    getContentBox: () => drawnBox,
    getCanvas: () => canvasElRef.current,
    // Colour table, iso-surface, slice and cutting-plane state kept in this component (saved with the view)
    getViewExtras: () => ({ colour, iso, slices, cut }),
    setViewExtras: (x) => {
      if (!x) { setColourState(DEFAULT_COLOUR); setIso((s) => ({ ...s, enabled: false, picked: null })); setSlices(DEFAULT_SLICES); setCut((s) => ({ ...s, enabled: false })); return; }
      if (x.colour) setColourState({ ...DEFAULT_COLOUR, ...x.colour });
      if (x.iso) setIso((s) => ({ ...s, ...x.iso }));
      if (x.slices) setSlices({ ...DEFAULT_SLICES, ...x.slices });
      if (x.cut) setCut((s) => ({ ...s, ...x.cut }));
    },
    exportImage: () => {
      const canvasEl = canvasElRef.current;
      if (!canvasEl) return;
      const url = canvasEl.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `subsurface-3d-${Date.now()}.png`;
      a.click();
    },
    exportModel: () => {
      const payload = {
        generatedAt: new Date().toISOString(),
        focusLine: focusLine ? { name: focusLine.name, method: focusLine.method } : null,
        colorMode,
        pointCount: filteredCloud.pointCount,
        totalPointCount: pointCloudData.pointCount,
        bboxMin: pointCloudData.bboxMin,
        bboxMax: pointCloudData.bboxMax,
        fenceModels: usableFences.map(f => ({ id: f.id, name: f.model?.name || f.id })),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `subsurface-3d-model-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
  }), [filteredCloud, pointCloudData, focusLine, colorMode, usableFences, colour, iso, slices, cut, fenceOrigin, drawnBox]);

  return (
    <div style={style} className="relative flex rounded-lg border border-border overflow-hidden shadow-lg bg-black">
      {/* Canvas area — never obscured by control chrome */}
      <div className="relative flex-1 min-w-0 h-full outline-none" tabIndex={0} onKeyDown={onCanvasKey}>
        <Canvas
          key={glState.epoch}
          camera={{ position: [0, 50, 150], fov: 60 }}
          className="w-full h-full"
          onCreated={({ gl }) => {
            if (glCleanup.current) glCleanup.current();
            glCleanup.current = watchContext(gl.domElement, glDispatch);
          }}
        >
          <Suspense fallback={null}>
            <CanvasCapture canvasElRef={canvasElRef} />
            {/* Orbit controls and camera presets: always mounted (not only with the point cloud layer), so the camera pose can be saved and restored */}
            <OrbitControls cameraCommand={cameraCommand} targetRef={targetRef} apiRef={orbitApiRef} sceneBounds={orbitBounds} />
            {/* Camera sync: look at the 2D map click position on first load */}
            {pointCloudData.lookAt ? (
              <CameraRig target={pointCloudData.lookAt} distance={Math.max(80, (pointCloudData.lineLength || 100) * 0.8)} />
            ) : modelFit && (
              <CameraRig target={modelFit.target} distance={modelFit.distance} />
            )}
            {syncFit && <CameraRig target={syncFit.target} distance={syncFit.distance} seq={syncFit.seq} />}
            {/* Lighting */}
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 5]} intensity={0.5} />

            {/* Point Cloud from AGI repo — filtered by threshold / slice / crop */}
            {showPointCloud && (
              <PointCloudRenderer
                data={filteredCloud}
                pointSize={sliceAxis !== 'none' ? 3.0 : 2.0}
                colorMode={colorMode}
                clippingPlanes={clippingPlanes}
              />
            )}

            <FenceColourContext.Provider value={fenceScale}>
            <VoxelSelectionContext.Provider value={voxelCtx}>
            {/* Fence diagrams — inversion models as vertical planes */}
            {showFences && showSections && fenceOrigin && !terrainDraped && usableFences.map(f => (
              <FencePlane key={f.id} entry={f} origin={fenceOrigin} opacity={fenceOpacity} onHover={handleHover3D} onHoverEnd={clearHover} clippingPlanes={clippingPlanes} />
            ))}
            {/* 3D inversion models — voxel clouds placed and rotated by their GeoRef */}
            {showFences && showVolumes && fenceOrigin && !terrainDraped && voxelModels.filter(f => !(slicing && slices.hideBlocks && f.id === volEntry?.id)).map(f => (
              <VoxelCloud key={`vox-${f.id}`} entry={f} opacity={fenceOpacity} onHover={handleHover3D} onHoverEnd={clearHover} clippingPlanes={clippingPlanes} />
            ))}
            {showFences && showSections && showTraces && fenceOrigin && !terrainDraped && <FenceTraces fences={usableFences} origin={fenceOrigin} />}
            {slicing && showFences && showVolumes && fenceOrigin && !terrainDraped && (
              <VolumeSlices items={sliceItems} sel={volSel} frame={sliceFrame} extent={sliceExtent} outsideOpacity={outsideOpacity}
                clippingPlanes={clippingPlanes} probe={probe} onPick={onSlicePick} onDrag={onSliceDrag} />
            )}
            <SliceContext.Provider value={sliceCtx}>
            {sceneExtras && sceneExtras({ origin: fenceOrigin })}
            </SliceContext.Provider>
            </VoxelSelectionContext.Provider>
            </FenceColourContext.Provider>

            <EnableClipping />
            {iso.enabled && isoInfo.meshes.map((m) => (
              <IsoSurfaceMesh key={m.index} mesh={m.mesh} colorMode={iso.colorMode} rgb={m.rgb} opacity={iso.opacity} clippingPlanes={clippingPlanes} />
            ))}
            {cut.enabled && (
              <CuttingPlaneGizmo centre={cutGeom.centre} normal={cutGeom.n0} size={cutGeom.size} slice={sliceTexture} />
            )}

            {/* View sync: hover highlight, section line outline, camera sync */}
            <ViewSync3D origin={fenceOrigin} hovered={hovered} sectionLine={sectionLine} sectionDepth={sectionDepth}
              syncCamera={syncCamera} cameraState={syncedCamera} publishCamera={publishCamera} targetRef={targetRef}
              extent={modelExtent} onFit={onSyncFit} />

            {/* Reference grid (ground surface) */}
            {showGrid && <gridHelper args={GRID_ARGS} position={[0, 0, 0]} />}
            {showAxes && <axesHelper args={AXES_ARGS} />}
          </Suspense>
        </Canvas>

        {glState.lost && (
          <button type="button" data-testid="gl-context-lost" onClick={() => glDispatch('retry')}
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            style={{ background: C.bg2, color: C.text0, border: 'none', fontSize: 13, opacity: 0.95 }}>
            3D context lost — click to restore
          </button>
        )}

        {/* Compact, unobtrusive HUD — corner only, never covers the scene */}
        {showOverlay && (
        <div data-testid="scene-overlay" className="absolute top-3 left-3 bg-black/70 text-white px-3 py-2 rounded-md text-[11px] leading-snug space-y-0.5 pointer-events-none max-w-[260px]">
          {focusLine && (
            <div className="font-semibold text-accent">
              📍 {focusLine.name || 'Survey line'}{focusLine.method ? ` · ${focusLine.method}` : ''}
            </div>
          )}
          <div>Points: {filteredCloud.pointCount.toLocaleString()} / Color: {COLOR_MODE_LABELS[colorMode] || colorMode}</div>
          {pointCloudData.pointCount === 0 && usableFences.length === 0 && voxelModels.length === 0 && (
            <div data-testid="no-points-note" className="text-amber-300">
              No points to show: this view needs a survey line or a dataset with x, y and z (depth) values.
            </div>
          )}
        </div>
        )}

        {slicing && probe && (
          <div data-testid="slice-readout" className="absolute bottom-3 left-3 bg-black/70 text-white px-2 py-1 rounded-md text-[11px] pointer-events-none">
            {probe.text}
          </div>
        )}
        {slice2d && (
          <SliceImageModal slice={slice2d.slice} pixels={slice2dPixels} title={`${volEntry?.model?.name || 'Model'} slice ${slice2d.axis}`} onClose={() => setShow2d(false)} />
        )}

        {/* AGI Branding */}
        <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1 pointer-events-none">
          {textureAttribution && (
            <div data-testid="texture-attribution" className="bg-black/70 text-white px-2 py-0.5 rounded text-[10px]">
              Imagery: {textureAttribution}
            </div>
          )}
          <div className="bg-black/70 text-white px-3 py-1 rounded-md text-xs">
            Powered by AGI Point Cloud Visualizer
          </div>
        </div>
      </div>

      {/* Docked Volume panel — a normal side panel, not an overlay */}
      <VolumePanel
        collapsed={!showVolumePanel}
        onToggleCollapsed={() => setShowVolumePanel(v => !v)}
        threshold={threshold} setThreshold={setThreshold}
        sliceAxis={sliceAxis} setSliceAxis={setSliceAxis}
        slicePos={slicePos} setSlicePos={setSlicePos}
        sliceThickness={sliceThickness} setSliceThickness={setSliceThickness}
        cropX={cropX} setCropX={setCropX}
        cropZ={cropZ} setCropZ={setCropZ}
        showFences={showFences} setShowFences={setShowFences}
        fenceOpacity={fenceOpacity} setFenceOpacity={setFenceOpacity}
        usableFences={usableFences}
        filteredCount={filteredCloud.pointCount}
        totalCount={pointCloudData.pointCount}
        onResetFilters={onResetVolumeFilters}
        extra={(
          <>
          <VoxelVolumeControls models={voxelModels} modelId={volEntry?.id} setModelId={setVolModelId} renderMode={renderMode} setRenderMode={setRenderMode}
            outsideOpacity={outsideOpacity} setOutsideOpacity={setOutsideOpacity} sel={volSel} stats={volStats}
            canBlocks={!!volEntry && hasExtents(volEntry.cells)} onExportXyz={exportXyz} />
          <VolumeSliceControls hasModel={!!volEntry} slices={slices} setSlices={setSlices} onView2D={() => setShow2d(true)} />
          {hasLevelModels(usableFences, voxelModels) && <VolumeColourControls colour={colour} setColour={setColour} range={volRange} scale={volScale} />}
          <VolumeIsoControls models={voxelModels} iso={iso} setIso={setIso} isoInfo={isoInfo} onExport={exportIso} scale={isoScale}
            cut={cut} setCut={setCut} cutRange={cutGeom.range} />
          </>
        )}
      />
    </div>
  );
});

export default SubsurfaceViewer3D;
